import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import React from 'react';

import { Card } from '@/cms/Card';
import { CMSSection } from '@/cms/CMSSection';
import type { MediaWrapperProps } from '@/cms/MediaWrapper';
import { MediaWrapper } from '@/cms/MediaWrapper';
import type { ProductCardProps } from '@/cms/ProductCard';
import { Container } from '@/components/Container';
import { Flex } from '@/components/Flex';
import { Row } from '@/components/Row';
import type {
  ContentfulCardFragment,
  ContentfulContentGroupFragment,
} from '@/graphql/contentful/api';
import { generateSectionKey } from '@/integrations/contentful';
import type {
  ContentfulData,
  ContentType,
} from '@/integrations/contentful/types';
import { generateDataTestId } from '@/lib/tests';
import type { WithHTMLProp } from '@/lib/types';

import type { CardProps } from '../Card';
import { Heading } from '../Heading';

import * as S from './ThreeColumnTeaser.styles';

type ThreeColumnTeaserWrapperProps = ContentfulContentGroupFragment &
  WithHTMLProp<'className'>;

export interface WallItemProps extends ContentfulCardFragment {
  preload?: boolean;
}
export const ThreeColumnTeaser = ({
  className,
  itemsCollection,
}: ThreeColumnTeaserWrapperProps) => {
  const Card = itemsCollection?.items[2]
    ? (itemsCollection?.items[2] as WallItemProps)
    : null;
  const List = itemsCollection?.items.filter(
    (item) => item?.__typename != 'Card',
  ) as MediaWrapperProps[];
  return (
    <CMSSection
      className={className}
      {...generateDataTestId('contentCollection', 'section')}
      data-test-id='ThreeColumnTeaserContentCollectionSection'
    >
      <Container>
        <S.MobileWrapper flexDirection='column'>
          <Flex flexDirection='row' justifyContent='center'>
            {Card?.placeholderHeading && (
              <Heading
                Heading={
                  (Card?.placeholderHeading as ContentfulCardFragment['placeholderHeading']) ??
                  undefined
                }
                data-test-id='ThreeColumnTeaserPlaceholderHeading'
                sizeDesktop={40}
                sizeMobile={34}
              />
            )}
          </Flex>
          {Card?.cardText && (
            <S.Subheader
              dangerouslySetInnerHTML={{
                __html: documentToHtmlString(Card.cardText.json),
              }}
              data-test-id='ThreeColumnTeaserCardText'
            />
          )}

          <Row>
            {List.map((item, i, array) => {
              const props: ItemData = item ? { ...item } : null;
              if (props) {
                props.preload = true;
              }
              const renderedItem = renderCollectionItem(props, i, array.length);
              return (
                renderedItem && (
                  <S.Col
                    key={generateSectionKey((item as ContentfulData)!, i)}
                    data-test-id={`ThreeColumnTeaserMediaWrapper-${i}`}
                    md={12 / array.length - 1}
                  >
                    {renderCollectionItem(item!, i, array.length)}
                  </S.Col>
                )
              );
            })}
          </Row>
          {Card?.urlText && (
            <S.collectionCTA
              data-test-id='ThreeColumnTeaserCollectionCTA'
              isFluid
              label={Card.urlText}
              linkProps={{ href: Card.url! }}
            />
          )}
        </S.MobileWrapper>
        <S.Wrapper>
          {itemsCollection?.items.filter(Boolean).map((item, i, array) => {
            const props = (item ? { ...item } : null) as ItemData;
            if (props) {
              props.preload = true;
            }
            const renderedItem = renderCollectionItem(props, i, array.length);
            return renderedItem ? (
              renderCollectionItem(item! as ItemData, i, array.length)
            ) : (
              <></>
            );
          })}
        </S.Wrapper>
      </Container>
    </CMSSection>
  );
};

type ItemData = CardProps | MediaWrapperProps | ProductCardProps | null;

export const renderCollectionItem = (
  data: ItemData,
  i: number,
  arrayLength: number,
) => {
  let props = null;
  const typename = data?.__typename as ContentType;
  switch (typename) {
    case 'Card':
      props = data as WallItemProps;
      return (
        <S.ParameterCol
          key={generateSectionKey(data! as ContentfulData, i)}
          data-test-id={`ThreeColumnTeaserParameterCol-${i}`}
          md={12 / arrayLength}
        >
          <Card {...props} preload />
        </S.ParameterCol>
      );

    case 'ImageWithFocalPoint':
      props = data as MediaWrapperProps;
      return (
        <S.Col
          key={generateSectionKey(data! as ContentfulData, i)}
          data-test-id={`ThreeColumnTeaserCol-${i}`}
          md={12 / arrayLength}
        >
          <MediaWrapper {...props} mediaWrapperType='ThreeColumnTeaser' />
        </S.Col>
      );

    default:
      return null;
  }
};

ThreeColumnTeaser.displayName = 'ThreeColumnTeaser';
